/**
 * CompassRoom — Mission Control Phase E.
 *
 * Section 1: North Star Metrics (live platform KPIs vs. goals)
 * Section 2: Standing Policies Editor (business rules agents read before acting)
 *
 * Data:
 *   GET /api/admin/ops/compass   — polled every 60s
 *   GET /api/admin/ops/policies  — polled every 30s
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Compass,
  RefreshCw,
  Plus,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Target,
} from "lucide-react";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

// ── Auth helper ────────────────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Formatting helpers ─────────────────────────────────────────────────────────

function fmtValue(value, unit) {
  if (value == null) return "—";
  if (unit === "usd") {
    return (
      "$" +
      Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }
  if (unit === "percent") {
    return Number(value).toFixed(1) + "%";
  }
  return Number(value).toLocaleString();
}

function fmtGoal(value, goal, unit) {
  const v = fmtValue(value, unit);
  const g = fmtValue(goal, unit);
  return `${v} / ${g} goal`;
}

function fmtTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// ── NorthStarCard ──────────────────────────────────────────────────────────────

function NorthStarCard({ metric }) {
  const { value, goal, unit, label } = metric;

  const pct = goal != null ? Math.min((value / goal) * 100, 100) : null;
  const overGoal = goal != null && value / goal >= 1;

  const fillColor = overGoal ? "#E07A4E" : "var(--color-green)";

  return (
    <div className="mc-stat-card mc-stat-card--accent">
      <span className="mc-stat-value">{fmtValue(value, unit)}</span>
      <span className="mc-stat-label">{label}</span>

      {pct !== null && (
        <div style={{ marginTop: 10 }}>
          {/* Progress track */}
          <div
            role="progressbar"
            aria-valuenow={value}
            aria-valuemax={goal}
            aria-label={label + " progress"}
            style={{
              width: "100%",
              height: 4,
              borderRadius: 2,
              background: "rgba(255,255,255,0.07)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                borderRadius: 2,
                background: fillColor,
                transition: "width 400ms ease",
              }}
            />
          </div>
          {/* Goal caption */}
          <span
            className="mc-mono"
            style={{
              display: "block",
              fontSize: 10,
              color: "var(--color-slate-500)",
              marginTop: 4,
            }}
          >
            {fmtGoal(value, goal, unit)}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Ghost / skeleton ───────────────────────────────────────────────────────────

function GhostStatCard() {
  return (
    <div
      className="mc-stat-card mc-agent-tile-ghost"
      aria-hidden="true"
      style={{ minHeight: 110 }}
    />
  );
}

function GhostPolicyRow() {
  return (
    <div
      aria-hidden="true"
      style={{
        height: 40,
        borderRadius: 4,
        background: "var(--mc-surface)",
        animation: "mc-skeleton-shimmer 1.4s ease-in-out infinite",
        marginBottom: 6,
      }}
    />
  );
}

// ── Toggle switch ──────────────────────────────────────────────────────────────

function PolicyToggle({ enabled, onChange, disabled }) {
  return (
    <button
      role="switch"
      aria-checked={enabled}
      onClick={onChange}
      disabled={disabled}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        width: 32,
        height: 18,
        borderRadius: 9,
        background: enabled ? "var(--color-green)" : "var(--color-slate-600, #4B5563)",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: 0,
        flexShrink: 0,
        transition: "background 150ms ease",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: "absolute",
          left: enabled ? 16 : 2,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "white",
          transition: "left 150ms ease",
          display: "block",
        }}
      />
    </button>
  );
}

// ── Add Policy Form ────────────────────────────────────────────────────────────

const CATEGORIES = ["finance", "legal", "ops", "outreach", "quality", "vetting"];

function AddPolicyForm({ onSave, onCancel }) {
  const [ruleKey, setRuleKey] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("outreach");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function handleSave() {
    if (!ruleKey.trim() || !title.trim()) return;

    let parsed;
    try {
      const num = Number(value);
      parsed = isNaN(num) || value.trim() === "" ? value.trim() : num;
    } catch {
      parsed = value.trim();
    }

    setSaving(true);
    setErr(null);
    try {
      await axios.post(
        `${API}/api/admin/ops/policies`,
        {
          rule_key: ruleKey.trim(),
          title: title.trim(),
          category,
          value_json: { value: parsed },
          enabled: true,
        },
        { headers: authHeaders() }
      );
      onSave();
    } catch (e) {
      setErr(e?.response?.data?.error ?? "Save failed");
      setSaving(false);
    }
  }

  const inputStyle = {
    background: "transparent",
    border: "1px solid var(--mc-border)",
    borderRadius: 3,
    padding: "4px 8px",
    color: "var(--mc-text)",
    fontFamily: "var(--font-sans)",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  return (
    <div
      style={{
        border: "1px solid var(--mc-border)",
        borderRadius: 6,
        padding: "16px",
        background: "var(--mc-surface)",
        marginTop: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--color-slate-400)",
        }}
      >
        New Policy
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 10,
              color: "var(--color-slate-500)",
              fontFamily: "var(--font-sans)",
              marginBottom: 3,
            }}
          >
            rule_key
          </label>
          <input
            type="text"
            placeholder="category.rule_name"
            value={ruleKey}
            onChange={(e) => setRuleKey(e.target.value)}
            style={{ ...inputStyle, fontFamily: "var(--font-mono)", fontSize: 12 }}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 10,
              color: "var(--color-slate-500)",
              fontFamily: "var(--font-sans)",
              marginBottom: 3,
            }}
          >
            title
          </label>
          <input
            type="text"
            placeholder="Human-readable title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 10,
              color: "var(--color-slate-500)",
              fontFamily: "var(--font-sans)",
              marginBottom: 3,
            }}
          >
            category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={selectStyle}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 10,
              color: "var(--color-slate-500)",
              fontFamily: "var(--font-sans)",
              marginBottom: 3,
            }}
          >
            value
          </label>
          <input
            type="text"
            placeholder="e.g. 2"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{ ...inputStyle, fontFamily: "var(--font-mono)", fontSize: 12 }}
          />
        </div>
      </div>

      {err && (
        <p
          style={{
            margin: 0,
            fontSize: 12,
            color: "var(--color-critical)",
            fontFamily: "var(--font-sans)",
          }}
        >
          {err}
        </p>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: "5px 14px",
            borderRadius: 4,
            border: "1px solid var(--mc-border)",
            background: "transparent",
            color: "var(--color-slate-400)",
            cursor: "pointer",
            fontFamily: "var(--font-sans)",
            fontSize: 12,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !ruleKey.trim() || !title.trim()}
          style={{
            padding: "5px 14px",
            borderRadius: 4,
            border: "1px solid var(--color-green)",
            background: saving ? "transparent" : "rgba(23,128,61,0.1)",
            color: "var(--color-green)",
            cursor: saving ? "not-allowed" : "pointer",
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            opacity: saving || !ruleKey.trim() || !title.trim() ? 0.5 : 1,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ── PolicyRow ──────────────────────────────────────────────────────────────────

function PolicyRow({ policy, expanded, onToggleExpand, onToggleEnabled, onSaveValue }) {
  const { id, rule_key, title, description, enabled, value_json } = policy;

  // Derive display string from value_json
  const rawValue =
    value_json != null && typeof value_json === "object" && "value" in value_json
      ? value_json.value
      : value_json;
  const initDisplay =
    rawValue != null
      ? typeof rawValue === "object"
        ? JSON.stringify(rawValue)
        : String(rawValue)
      : "";

  const [draft, setDraft] = useState(initDisplay);
  const [saving, setSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  const priorRef = useRef(initDisplay);
  const errorTimerRef = useRef(null);

  // Keep draft in sync if parent updates policy (e.g. after refetch)
  useEffect(() => {
    const next =
      value_json != null && typeof value_json === "object" && "value" in value_json
        ? value_json.value
        : value_json;
    const nextStr =
      next != null
        ? typeof next === "object"
          ? JSON.stringify(next)
          : String(next)
        : "";
    setDraft(nextStr);
    priorRef.current = nextStr;
  }, [value_json]);

  async function commitValue() {
    if (draft === priorRef.current) return;

    const num = Number(draft);
    const parsed = draft.trim() !== "" && !isNaN(num) ? num : draft.trim();

    setSaving(true);
    try {
      await onSaveValue(id, { value: parsed });
      priorRef.current = draft;
    } catch {
      // revert + show error
      setDraft(priorRef.current);
      setHasError(true);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => setHasError(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.target.blur();
    }
    if (e.key === "Escape") {
      setDraft(priorRef.current);
      e.target.blur();
    }
  }

  return (
    <div
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}
    >
      {/* Main row */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          padding: "10px 0",
        }}
      >
        {/* Status dot — click to toggle enabled */}
        <button
          aria-label={enabled ? "Disable policy" : "Enable policy"}
          onClick={() => onToggleEnabled(id, !enabled)}
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: enabled ? "var(--color-green)" : "var(--color-slate-500, #6B7280)",
            border: "none",
            cursor: "pointer",
            padding: 0,
            flexShrink: 0,
            transition: "background 150ms ease",
          }}
        />

        {/* Title — click to expand/collapse */}
        <button
          onClick={() => onToggleExpand(id)}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 5,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--mc-text)",
            fontFamily: "var(--font-sans)",
            fontSize: 14,
            fontWeight: 500,
            textAlign: "left",
            padding: 0,
          }}
        >
          {expanded ? (
            <ChevronDown size={12} style={{ flexShrink: 0, color: "var(--color-slate-500)" }} />
          ) : (
            <ChevronRight size={12} style={{ flexShrink: 0, color: "var(--color-slate-500)" }} />
          )}
          <span>{title}</span>
        </button>

        {/* Saving indicator */}
        {saving && (
          <span
            style={{
              fontSize: 10,
              color: "var(--color-slate-400)",
              fontFamily: "var(--font-sans)",
              animation: "mc-pulse 1s ease-in-out infinite",
              flexShrink: 0,
            }}
          >
            Saving…
          </span>
        )}

        {/* Value input */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitValue}
            onKeyDown={handleKeyDown}
            aria-label={`Value for ${title}`}
            style={{
              width: 80,
              background: "transparent",
              border: `1px solid ${hasError ? "var(--color-critical)" : "var(--mc-border)"}`,
              borderRadius: 3,
              padding: "3px 6px",
              color: "var(--mc-text)",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              textAlign: "right",
              outline: "none",
              transition: "border-color 150ms ease",
            }}
          />
          {hasError && (
            <span
              aria-label="Save error"
              style={{
                position: "absolute",
                right: -16,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--color-critical)",
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              !
            </span>
          )}
        </div>

        {/* Toggle */}
        <PolicyToggle
          enabled={enabled}
          onChange={() => onToggleEnabled(id, !enabled)}
        />
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: "0 0 10px 23px" }}>
          <span
            className="mc-mono"
            style={{
              display: "block",
              fontSize: 10,
              color: "var(--color-slate-600)",
              marginBottom: description ? 5 : 0,
            }}
          >
            {rule_key}
          </span>
          {description && (
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: "var(--color-slate-400)",
                fontFamily: "var(--font-sans)",
                lineHeight: 1.5,
              }}
            >
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── PolicyGroup ────────────────────────────────────────────────────────────────

function PolicyGroup({
  category,
  policies,
  expandedPolicies,
  onToggleExpand,
  onToggleEnabled,
  onSaveValue,
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      {/* Category header */}
      <div
        style={{
          borderBottom: "1px solid var(--mc-border)",
          marginBottom: 8,
          paddingBottom: 4,
        }}
      >
        <span
          className="mc-mono"
          style={{
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "var(--color-slate-400)",
          }}
        >
          {category}
        </span>
      </div>

      {/* Policy rows */}
      {policies.map((p) => (
        <PolicyRow
          key={p.id}
          policy={p}
          expanded={expandedPolicies.has(p.id)}
          onToggleExpand={onToggleExpand}
          onToggleEnabled={onToggleEnabled}
          onSaveValue={onSaveValue}
        />
      ))}
    </div>
  );
}

// ── Error block ────────────────────────────────────────────────────────────────

function ErrorBlock({ message, onRetry }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        padding: "40px 0",
        color: "var(--color-critical)",
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        textAlign: "center",
      }}
    >
      <AlertCircle size={24} />
      <span>{message}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: "6px 16px",
            border: "1px solid var(--color-critical)",
            borderRadius: 4,
            background: "transparent",
            color: "var(--color-critical)",
            cursor: "pointer",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function CompassRoom() {
  // ── Compass metrics state ────────────────────────────────────────────────────
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);
  const [computedAt, setComputedAt] = useState(null);

  // ── Policies state ───────────────────────────────────────────────────────────
  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [policiesError, setPoliciesError] = useState(null);

  // ── Interaction state ────────────────────────────────────────────────────────
  const [expandedPolicies, setExpandedPolicies] = useState(new Set());
  const [showAddForm, setShowAddForm] = useState(false);

  // ── Fetch compass ────────────────────────────────────────────────────────────

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/ops/compass`, {
        headers: authHeaders(),
      });
      setMetrics(res.data.metrics ?? null);
      setComputedAt(res.data.computed_at ?? null);
      setMetricsError(null);
    } catch (err) {
      setMetricsError(err);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  // ── Fetch policies ───────────────────────────────────────────────────────────

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/ops/policies`, {
        headers: authHeaders(),
      });
      setPolicies(res.data.policies ?? []);
      setPoliciesError(null);
    } catch (err) {
      setPoliciesError(err);
    } finally {
      setPoliciesLoading(false);
    }
  }, []);

  // ── Polling setup ────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchMetrics();
    const metricsId = setInterval(fetchMetrics, 60_000);
    return () => clearInterval(metricsId);
  }, [fetchMetrics]);

  useEffect(() => {
    fetchPolicies();
    const policiesId = setInterval(fetchPolicies, 30_000);
    return () => clearInterval(policiesId);
  }, [fetchPolicies]);

  // ── Toggle expanded ──────────────────────────────────────────────────────────

  function handleToggleExpand(id) {
    setExpandedPolicies((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ── Toggle policy enabled ────────────────────────────────────────────────────

  async function handleToggleEnabled(id, newEnabled) {
    // Optimistic update
    setPolicies((prev) =>
      prev.map((p) => (p.id === id ? { ...p, enabled: newEnabled } : p))
    );
    try {
      await axios.put(
        `${API}/api/admin/ops/policies/${id}`,
        { enabled: newEnabled },
        { headers: authHeaders() }
      );
    } catch {
      // Revert
      setPolicies((prev) =>
        prev.map((p) => (p.id === id ? { ...p, enabled: !newEnabled } : p))
      );
    }
  }

  // ── Save policy value ────────────────────────────────────────────────────────

  async function handleSaveValue(id, valueJson) {
    await axios.put(
      `${API}/api/admin/ops/policies/${id}`,
      { value_json: valueJson },
      { headers: authHeaders() }
    );
    // Update local state on success
    setPolicies((prev) =>
      prev.map((p) => (p.id === id ? { ...p, value_json: valueJson } : p))
    );
  }

  // ── After adding a policy ────────────────────────────────────────────────────

  async function handleAddSave() {
    setShowAddForm(false);
    setPoliciesLoading(false); // don't re-show full skeleton
    await fetchPolicies();
  }

  // ── Derived: grouped + sorted policies ──────────────────────────────────────

  const groupedPolicies = policies.reduce((acc, p) => {
    const cat = p.category ?? "ops";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const sortedCategories = Object.keys(groupedPolicies).sort();
  sortedCategories.forEach((cat) => {
    groupedPolicies[cat].sort((a, b) => a.rule_key.localeCompare(b.rule_key));
  });

  // ── Agent cost (5th metric, standalone) ─────────────────────────────────────
  const agentCostMetric = metrics?.agent_cost_24h ?? null;

  // ── Four primary KPI metrics ─────────────────────────────────────────────────
  const primaryKeys = [
    "active_experts",
    "gmv_30d",
    "expert_utilization",
    "returning_seekers_30d",
  ];
  const primaryMetrics = primaryKeys
    .map((k) => (metrics ? metrics[k] : null))
    .filter(Boolean);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <section className="mc-room mc-room--compass" aria-label="Compass room">
      {/* ── Room header ── */}
      <header className="mc-room-header">
        <div>
          <p className="mc-room-eyebrow">ROOM · COMPASS</p>
          <h1 className="mc-room-title">Compass</h1>
        </div>
        <button
          className="mc-chip mc-chip--neutral"
          onClick={() => {
            fetchMetrics();
            fetchPolicies();
          }}
          aria-label="Refresh compass data"
          style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
        >
          <RefreshCw size={11} />
          refresh
        </button>
      </header>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1: NORTH STAR METRICS
          ══════════════════════════════════════════════════════════════════ */}

      {/* Section header */}
      <div className="mc-section-header">
        <Target size={12} style={{ color: "var(--color-slate-400)" }} />
        <span className="mc-section-title">North Star</span>
        {computedAt && (
          <span
            className="mc-mono"
            style={{
              marginLeft: "auto",
              fontSize: 10,
              color: "var(--color-slate-500)",
            }}
          >
            updated {fmtTimestamp(computedAt)}
          </span>
        )}
      </div>

      <p
        style={{
          margin: "0 0 16px",
          fontSize: 13,
          color: "var(--color-slate-500)",
          fontFamily: "var(--font-sans)",
        }}
      >
        Live platform metrics vs. goals. Updated every minute.
      </p>

      {/* Metrics content */}
      {metricsLoading ? (
        <div
          className="mc-stat-grid"
          style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
        >
          <GhostStatCard />
          <GhostStatCard />
          <GhostStatCard />
          <GhostStatCard />
        </div>
      ) : metricsError && !metrics ? (
        <ErrorBlock
          message="Failed to load platform metrics."
          onRetry={fetchMetrics}
        />
      ) : (
        <>
          {/* 4-col grid */}
          <div
            className="mc-stat-grid"
            style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
          >
            {primaryMetrics.map((m) => (
              <NorthStarCard key={m.label} metric={m} />
            ))}
          </div>

          {/* Agent cost — standalone stat below the grid */}
          {agentCostMetric && (
            <p
              className="mc-mono"
              style={{
                margin: "12px 0 0",
                fontSize: 12,
                color: "var(--color-slate-400)",
              }}
            >
              Agent Spend:{" "}
              <span style={{ color: "var(--mc-text)" }}>
                {fmtValue(agentCostMetric.value, agentCostMetric.unit)}
              </span>{" "}
              / 24h
            </p>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2: STANDING POLICIES
          ══════════════════════════════════════════════════════════════════ */}

      {/* Section header */}
      <div className="mc-section-header" style={{ marginTop: 32 }}>
        <Compass size={12} style={{ color: "var(--color-slate-400)" }} />
        <span className="mc-section-title">Standing Policies</span>
        <span
          style={{
            fontSize: 11,
            color: "var(--color-slate-500)",
            fontFamily: "var(--font-sans)",
            marginLeft: 4,
          }}
        >
          Business rules agents read before acting.
        </span>
        <button
          className="mc-chip mc-chip--green"
          onClick={() => setShowAddForm((v) => !v)}
          aria-label="Add policy"
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Plus size={10} />
          Add
        </button>
      </div>

      {/* Policies content */}
      {policiesLoading ? (
        <div style={{ marginTop: 8 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <GhostPolicyRow key={i} />
          ))}
        </div>
      ) : policiesError && policies.length === 0 ? (
        <ErrorBlock
          message="Failed to load policies."
          onRetry={fetchPolicies}
        />
      ) : policies.length === 0 && !showAddForm ? (
        <div className="mc-empty-state">
          No policies configured. Add the first one.
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          {sortedCategories.map((cat) => (
            <PolicyGroup
              key={cat}
              category={cat}
              policies={groupedPolicies[cat]}
              expandedPolicies={expandedPolicies}
              onToggleExpand={handleToggleExpand}
              onToggleEnabled={handleToggleEnabled}
              onSaveValue={handleSaveValue}
            />
          ))}
        </div>
      )}

      {/* Add policy inline form */}
      {showAddForm && (
        <AddPolicyForm
          onSave={handleAddSave}
          onCancel={() => setShowAddForm(false)}
        />
      )}
    </section>
  );
}
